import { useTranslation } from 'react-i18next';
import { Button, ButtonVariants, IconNames, Input } from '@triumph/design-system';
import { isValidEmail } from '@/app/shared/utils/validation';
import { DestinationRow } from '../communicationPreferences.types';
import {
  AddButtonRow,
  DestinationInputWrapper,
  DestinationRowContainer,
  HelpText,
} from '../CommunicationPreferences.StyledComponents';
import { SectionHeading } from './SectionHeading';

interface DeliveryDestinationsProps {
  rows: DestinationRow[];
  showValidation: boolean;
  onChangeEmail: (key: string, email: string) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
}

export const DeliveryDestinations: React.FC<DeliveryDestinationsProps> = ({
  rows,
  showValidation,
  onChangeEmail,
  onRemove,
  onAdd,
}) => {
  const { t } = useTranslation('communicationPreferences');

  const rowError = (row: DestinationRow): string | undefined => {
    if (!showValidation) return undefined;
    const email = row.emailAddress.trim();
    if (!email) return undefined;
    return isValidEmail(email) ? undefined : t('destinations.invalidEmail');
  };

  return (
    <div data-testid="comm-pref-destinations">
      <SectionHeading
        icon={IconNames.Office_EnvelopeLetter}
        iconId="comm-pref-destinations-icon"
        label={t('destinations.title')}
      />
      {rows.map((row, index) => {
        const error = rowError(row);
        return (
          <DestinationRowContainer key={row.key}>
            <DestinationInputWrapper>
              <Input
                id={`comm-pref-destination-${index}`}
                value={row.emailAddress}
                placeholder={t('destinations.emailPlaceholder')}
                onChange={(e) => onChangeEmail(row.key, e.target.value)}
                rightIcon={{ iconName: IconNames.Interface_Trash, show: true }}
                iconAction={() => onRemove(row.key)}
                iconAltText={t('destinations.removeAltText')}
                iconContainerTestId={`comm-pref-destination-remove-${index}`}
                inputTextfieldTestId={`comm-pref-destination-input-${index}`}
                messageType={error ? 'error' : undefined}
                validationMessage={error}
              />
            </DestinationInputWrapper>
          </DestinationRowContainer>
        );
      })}
      <AddButtonRow>
        <Button
          variant={ButtonVariants.textButton}
          icon={IconNames.AddRemove_AddCircle}
          iconLeft
          onClick={onAdd}
          data-testid="comm-pref-add-destination"
        >
          {t('destinations.addAnother')}
        </Button>
      </AddButtonRow>
      <HelpText>{t('destinations.helpText')}</HelpText>
    </div>
  );
};
